import { checkErrors, getDefaultOptions } from "../../services/Category";
import {
  ConnectorState,
  Candidate,
  CandidateFilters,
  CandidatePage,
  ContractedProperty,
  ContractState,
  OnboardingStatus,
  Selection,
} from "./types";

/**
 * What the server reads as "no value recorded" in a `stars` or `rating` filter.
 * Must match `UNKNOWN_PARAM` in `api/competitors/views.py`.
 */
export const UNKNOWN_FILTER = "unknown";

/** Mirrors `search.MIN_TERM_LENGTH`: one character matches a third of the catalogue. */
export const MIN_SEARCH_LENGTH = 2;

const BASE = "/api/competitors";

/** For the routes this page needs that are not registered under `competitors/`. */
const API_ROOT = "/api";

async function request<T>(
  path: string,
  options: RequestInit = {},
  // ⚠️ The two mapping routes sit on the shared router at `/api/mapping/`, not under
  // `/api/competitors/`. Same origin, different prefix — hence a flag rather than a
  // second helper that would duplicate the error handling.
  fromApiRoot = false
): Promise<T> {
  const response = await fetch(
    new URL(
      `${fromApiRoot ? API_ROOT : BASE}${path}`,
      window.origin
    ).toString(),
    getDefaultOptions(options)
  );
  checkErrors(response);
  const text = await response.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

interface ContractPayload {
  property_id: number;
  property_code: string;
  property_name: string;
  latitude: number | null;
  longitude: number | null;
  radius_km: number;
  competitor_limit: number;
  self_connector: boolean;
  state: ContractState;
}

interface CandidatePayload {
  competitor: {
    id: number;
    identifier: string;
    name: string;
    city: string | null;
    latitude: number | null;
    longitude: number | null;
    interest_rating: number;
    meta: {
      stars: number | null;
      rating: number | null;
      reviews_count: number | null;
    } | null;
  };
  distance_km: number;
  is_priceable: boolean;
}

interface SelectionPayload {
  id: number;
  competitor_code: string;
  name: string;
  short_name: string;
  stars: number | null;
  is_active: boolean;
  is_shown: boolean;
  is_self: boolean;
  onboarding_status: OnboardingStatus | "";
  is_sweep: boolean;
  competitor_property_datafeed_id: number | null;
  connectors?: ConnectorStatePayload[];
}

interface ConnectorStatePayload {
  family: string;
  target: string;
  status: ConnectorState["status"];
  is_active: boolean;
  exists: boolean;
}

const toProperty = (payload: ContractPayload): ContractedProperty => ({
  propertyId: payload.property_id,
  propertyCode: payload.property_code,
  propertyName: payload.property_name,
  latitude: payload.latitude,
  longitude: payload.longitude,
  radiusKm: payload.radius_km,
  competitorLimit: payload.competitor_limit,
  selfConnector: Boolean(payload.self_connector),
  state: payload.state,
});

const toCandidate = (payload: CandidatePayload): Candidate => ({
  id: payload.competitor.id,
  identifier: payload.competitor.identifier,
  name: payload.competitor.name,
  city: payload.competitor.city,
  latitude: payload.competitor.latitude,
  longitude: payload.competitor.longitude,
  interestRating: payload.competitor.interest_rating,
  stars: payload.competitor.meta?.stars ?? null,
  rating: payload.competitor.meta?.rating ?? null,
  reviewsCount: payload.competitor.meta?.reviews_count ?? null,
  distanceKm: payload.distance_km,
  isPriceable: payload.is_priceable ?? false,
});

const toSelection = (payload: SelectionPayload): Selection => ({
  id: payload.id,
  competitorCode: payload.competitor_code,
  name: payload.name,
  shortName: payload.short_name,
  stars: payload.stars,
  isActive: payload.is_active,
  isShown: payload.is_shown,
  isSelf: Boolean(payload.is_self),
  onboardingStatus: payload.onboarding_status || "",
  isSweep: payload.is_sweep,
  competitorPropertyDatafeedId: payload.competitor_property_datafeed_id,
  // Absent rather than empty when the sweep could not be reached — the view drops the
  // annotation rather than the page. `connectorHealth` treats both as "nothing to say".
  connectors: (payload.connectors ?? []).map((connector) => ({
    family: connector.family,
    target: connector.target,
    status: connector.status,
    isActive: connector.is_active,
    exists: connector.exists,
  })),
});

export async function fetchContractedProperties(): Promise<
  ContractedProperty[]
> {
  const body = await request<{ results: ContractPayload[] }>("/contracts/");
  return (body.results ?? []).map(toProperty);
}

export async function fetchCandidates(
  propertyId: number,
  radiusKm: number,
  limit: number,
  offset: number,
  filters: CandidateFilters
): Promise<CandidatePage> {
  const params = new URLSearchParams({
    property: String(propertyId),
    radius_km: String(radiusKm),
    limit: String(limit),
    offset: String(offset),
  });
  // Repeated parameters rather than a comma-joined list: `getlist` on the server
  // reads them natively, and a value that is itself a word — `unknown` — would make
  // a joined string ambiguous the first time someone hand-edits a URL.
  //
  // ⚠️ An empty selection sends nothing, which the server reads as "no filter on
  // this axis" rather than "match nothing". Every box unchecked therefore shows
  // everything, which is the only behaviour that lets someone recover from having
  // unchecked the lot.
  // Only sent when it is long enough to mean something: the server ignores a
  // shorter term anyway, so sending it would spend a request on the unfiltered
  // answer the client already has.
  if (filters.term.trim().length >= MIN_SEARCH_LENGTH) {
    params.set("q", filters.term.trim());
  }
  filters.interestRatings.forEach((value) =>
    params.append("interest_rating", String(value))
  );
  filters.stars.forEach((value) =>
    params.append("stars", String(value ?? UNKNOWN_FILTER))
  );
  filters.ratings.forEach((value) =>
    params.append("rating", String(value ?? UNKNOWN_FILTER))
  );
  const body = await request<{
    matches: CandidatePayload[];
    total: number;
    offset: number;
    limit: number;
    has_more: boolean;
    error?: string;
  }>(`/candidates/?${params.toString()}`);
  return {
    items: (body.matches ?? []).map(toCandidate),
    total: body.total,
    offset: body.offset,
    limit: body.limit,
    hasMore: body.has_more,
    error: body.error,
  };
}

export async function fetchSelection(propertyId: number): Promise<Selection[]> {
  const params = new URLSearchParams({ property: String(propertyId) });
  const body = await request<{ results: SelectionPayload[] }>(
    `/selection/?${params.toString()}`
  );
  return (body.results ?? []).map(toSelection);
}

export async function addSelection(
  propertyId: number,
  competitorId: number
): Promise<Selection> {
  const payload = await request<SelectionPayload>("/selection/", {
    method: "POST",
    body: JSON.stringify({ property: propertyId, competitor_id: competitorId }),
  });
  return toSelection(payload);
}

/**
 * Hide a tracked competitor, or take it off the compset without removing it.
 *
 * ⚠️ Hiding is the reversible half. `stopTracking` is the other one, and the two are
 * deliberately different weights: this changes what a client sees, that one removes
 * the rows.
 */
/**
 * Stop tracking a competitor: the rows, the datafeeds and the tenant's prices for it.
 *
 * ⚠️ **Not reversible from here, and it does not have to be.** The price *history*
 * lives centrally in the sweep, keyed by competitor rather than by who tracked it, so
 * re-selecting the same competitor brings it back. What is lost is this property's
 * own mapping of it — which room stands for which — and that is a human judgement
 * nobody else holds. Hence the confirmation in `SelectedList`.
 *
 * ⚠️ Answers 204 with no body, so there is nothing to parse and nothing to return.
 * The caller refetches; the server has already released the connector behind it.
 */
export async function stopTracking(selectionId: number): Promise<void> {
  await request<void>(`/selection/${selectionId}/`, { method: "DELETE" });
}

/**
 * Track the customer's own property, so the compset can include itself.
 *
 * ⚠️ Sends only the property. Which competitor it *is* comes from the contract, where
 * Revbell resolved it and where a human can correct it — a competitor id from the
 * browser would file the customer's own parity data against another hotel.
 */
export async function addSelfSelection(propertyId: number): Promise<Selection> {
  const payload = await request<SelectionPayload>(`/selection/self/`, {
    method: "POST",
    body: JSON.stringify({ property: propertyId }),
  });
  return toSelection(payload);
}

export async function updateSelection(
  selectionId: number,
  changes: { isActive?: boolean; isShown?: boolean }
): Promise<Selection> {
  const body: Record<string, boolean> = {};
  if (changes.isActive !== undefined) body.is_active = changes.isActive;
  if (changes.isShown !== undefined) body.is_shown = changes.isShown;
  const payload = await request<SelectionPayload>(
    `/selection/${selectionId}/`,
    {
      method: "PATCH",
      body: JSON.stringify(body),
    }
  );
  return toSelection(payload);
}
